import { graphql } from 'client/gql';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { CONNECT_DATE_TEMPLATE } from 'utils/connectLib';
import { SimpleEvent } from '../types/eventTypes';
import { getDurationInMinutes } from '../utils/eventUtils';

const HomeworkEventsQuery = graphql(`
  query HomeworkEventsData($calendarEventWhere: CalendarEventWhere!) {
    calendarEvents(where: $calendarEventWhere) {
      __typename
      uuid
      title
      start
      duration
      classbookHomework
      holder {
        __typename
        ... on Group {
          __typename
          uuid
          grades
          subject {
            __typename
            uuid
            name
            shortName
            color
          }
        }
      }
    }
  }
`);

type UseHomeworkEventsProps = {
  profileUuid: string;
  timespan?: {
    start: Date | null;
    end: Date | null;
  };
};

export type HomeworkEvent = SimpleEvent & {
  homework: string | null;
  subject: {
    uuid: string;
    name: string;
    shortName: string;
    color: string;
  };
};

export const useHomeworkEvents = ({ profileUuid, timespan }: UseHomeworkEventsProps) => {
  const { t } = useTranslation();

  const timespanStart = timespan && timespan.start ? timespan.start : new Date();
  const timespanEnd = timespan && timespan.end ? timespan.end : new Date();
  const daysDifference = Math.abs(dayjs(timespanStart).diff(timespanEnd, 'days')) + 1;
  const timespanDates: Date[] = [];

  for (let i = 0; i < daysDifference; i++) {
    timespanDates.push(dayjs(timespanStart).startOf('day').add(i, 'days').toDate());
  }

  const [{ data }] = useQuery({
    query: HomeworkEventsQuery,
    variables: {
      calendarEventWhere: {
        categories_INCLUDES: 'lesson',
        attendees_SOME: { uuid_CONTAINS: profileUuid },
        daysConnection_SOME: {
          OR: timespanDates.map((d) => ({
            node: { date_STARTS_WITH: dayjs(d).format(CONNECT_DATE_TEMPLATE) },
          })),
        },
      },
    },
  });

  const homeworkEvents: HomeworkEvent[] =
    data?.calendarEvents.map((e) => {
      const duration = getDurationInMinutes(e.duration);
      const start = dayjs(e.start);
      const end = start.add(duration, 'minutes');

      const group = e.holder.__typename === 'Group' ? e.holder : null;
      const subject = group && group.subject ? group.subject : null;

      return {
        uuid: e.uuid,
        title: e.title ?? t('events.title', { count: 1 }),
        start: start.toDate(),
        end: end.toDate(),
        homework: e.classbookHomework ?? null,
        subject: {
          uuid: subject?.uuid ?? '',
          name: subject?.name ?? t('subjects.title', { count: 1 }),
          shortName: subject?.shortName ?? t('subjects.title', { count: 1 }),
          color: subject?.color ?? 'var(--color-white)',
        },
      };
    }) ?? [];

  return { homeworkEvents };
};
